// BUILD-042 — HR Administration unified layout. Friendly display names for HR capability codes
// and scope values, per the approved decision: "show friendly capability and scope labels plus
// plain-language explanation... Exact capability codes may remain in expandable technical
// details. Do not change capability keys, scope mapping, grant/revoke payloads, authority or
// visibility." This module is PRESENTATION ONLY — the underlying strings sent to
// createHrAccessGrant/stored in grants are UNCHANGED (still exactly 'hr.team.view',
// 'direct_reports', etc.); this just maps them to a readable label for display, with the raw
// code always still available in a "Technical details" disclosure (see AccessPanel.tsx).
export const CAPABILITY_LABELS: Record<string, string> = {
  'hr.record.self.view': 'View own HR record',
  'hr.team.view': 'View team employment info',
  'hr.org.manage': 'Manage HR records (Administrator)',
  'hr.audit.view': 'View activity log',
  'hr.grant.manage': 'Grant/revoke access (Owner)',
  'hr.correction.review': 'Review profile correction requests',
  'hr.benefit.manage': 'Manage benefits',
  'hr.lifecycle.manage': 'Manage offboarding',
  'hr.checklist.manage': 'Manage checklist templates',
  'hr.checklist.assign': 'Manage employee checklists',
  'hr.document.manage': 'Manage forms, documents & equipment',
};

/** Capabilities that only make sense company-wide; the grant form pins their scope and the server refuses anything narrower. */
export const COMPANY_SCOPE_ONLY_CAPABILITIES: readonly string[] = ['hr.correction.review', 'hr.checklist.manage', 'hr.checklist.assign', 'hr.document.manage'];

export const CAPABILITY_EXPLANATION: Record<string, string> = {
  'hr.record.self.view': 'See their own HR record (every Employee already has this automatically).',
  'hr.team.view': 'See their direct reports’ basic employment info, as a Manager.',
  'hr.org.manage': 'Create/edit HR records and employment changes for other Employees, as HR Administration.',
  'hr.audit.view': 'Browse the HR audit history.',
  'hr.grant.manage': 'Grant or revoke these same capabilities to others — Owner-only.',
  'hr.correction.review': 'Decide employees’ profile correction requests. Whole company only. Adds no visibility of its own: a reviewer sees a request only for people (and facts) they can already see through HR Administration or as their manager.',
  'hr.benefit.manage': 'Keep benefit plan information and record each person’s benefit eligibility review. Whole company only. Not enrollment; adds no other HR access.',
  'hr.lifecycle.manage': 'Schedule or cancel a departure, archive or restore a person on the HR roster, and rehire. Whole company only. Never changes a login or app access.',
  'hr.checklist.manage': 'Create, edit, retire and restore checklist templates, and keep the responsibility labels their tasks are routed to. Whole company only. Adds no employee record access.',
  'hr.checklist.assign': 'Assign checklists to employees and record or complete their checklist tasks. Whole company only. Adds no access to private document content.',
  'hr.document.manage': 'Use the Form Library, assign and review employee documents, and issue equipment checkouts. Whole company only.',
};

export const SCOPE_LABELS: Record<string, string> = {
  self: 'Themselves only',
  direct_reports: 'Their direct reports',
  company: 'The whole company',
};

// ── Oct 20: the ONE-PERSON access editor ───────────────────────────────────────────────────────────────────────────────────────────
// The permissions the editor can change, each with the ONLY scopes that mean something for it, and plain wording. This mirrors the server's own table
// (index.ts ACCESS_EDITOR_CAPABILITIES), which re-checks every change; the screen offers nothing the server would refuse. It invents no permission: the
// five below are the same capabilities the one-grant-at-a-time form offered (hr.record.self.view is left out because every Employee already has it
// automatically; a stored grant of it, or of any capability not listed here, is shown read-only and never touched).
export type AccessScope = 'self' | 'direct_reports' | 'company';

export interface EditablePermission {
  capability: string;
  title: string;
  /** One plain sentence: what holding it lets the person do. */
  meaning: string;
  /** Valid scopes for this permission, in display order. A single entry means no choice is shown. */
  scopes: readonly AccessScope[];
  /** Only the Primary Owner may change this one. */
  ownerOnly?: boolean;
}

export const EDITABLE_PERMISSIONS: readonly EditablePermission[] = [
  {
    capability: 'hr.team.view', title: 'View team employment info',
    meaning: 'See basic employment info (title, department, manager, start date) for the people in the chosen group, as a manager. No pay, personal contacts, medical records or confidential cases.',
    scopes: ['direct_reports', 'company'],
  },
  {
    capability: 'hr.org.manage', title: 'Manage HR records (HR Administrator)',
    meaning: 'Set up and edit HR records and employment changes for other people, and keep departments and reporting lines. Whole company.',
    scopes: ['company'],
  },
  {
    capability: 'hr.audit.view', title: 'View activity log',
    meaning: 'Browse the HR activity log: who did what, and when, within the chosen group.',
    scopes: ['self', 'direct_reports', 'company'],
  },
  {
    capability: 'hr.correction.review', title: 'Review profile correction requests',
    meaning: 'Decide employees’ requests to correct their profile. Whole company only. Adds no visibility of its own: a reviewer sees a request only for people and facts they can already see.',
    scopes: ['company'],
  },
  {
    capability: 'hr.benefit.manage', title: 'Manage benefits',
    meaning: 'Keep the benefit plan information and record each person’s benefit eligibility review (Eligible, Not eligible or Not reviewed). Whole company only. This is not enrollment and gives no other HR access: no pay, personal contacts, documents or employment records.',
    scopes: ['company'],
  },
  {
    capability: 'hr.lifecycle.manage', title: 'Manage offboarding',
    meaning: 'Schedule or cancel a person’s departure, archive or restore them on the HR roster, and rehire them into a new employment period. Whole company only. It never changes anyone’s login or app access, and it adds no record access of its own.',
    scopes: ['company'],
  },
  {
    capability: 'hr.checklist.manage', title: 'Manage checklist templates',
    meaning: 'Create, edit, retire and restore checklist templates, and keep the responsibility labels that template tasks are routed to. Whole company only. It does not let the person assign checklists to employees or see their records.',
    scopes: ['company'],
  },
  {
    capability: 'hr.checklist.assign', title: 'Manage employee checklists',
    meaning: 'Assign checklists to employees and record or complete the tasks on the checklists of the people they are authorized for. Whole company only. It does not give access to private document content.',
    scopes: ['company'],
  },
  {
    capability: 'hr.document.manage', title: 'Manage forms, documents & equipment',
    meaning: 'Use the existing Form Library, assign and review employee documents, and issue equipment checkouts. Whole company only. It does not change checklist templates or anyone’s login or app access.',
    scopes: ['company'],
  },
  {
    capability: 'hr.grant.manage', title: 'Change access for others (Owner)',
    meaning: 'Give or take away the permissions on this page for other people. Only the Primary Owner can change this one.',
    scopes: ['company'], ownerOnly: true,
  },
];

/** Plain-language scope wording FOR one permission (the generic SCOPE_LABELS read oddly for some, e.g. "Themselves only" on the activity log). */
export const EDITABLE_SCOPE_LABELS: Record<string, Partial<Record<AccessScope, string>>> = {
  'hr.team.view': { direct_reports: 'People who report to them', company: 'Everyone in the company' },
  'hr.org.manage': { company: 'Everyone in the company' },
  'hr.audit.view': { self: 'Activity about themselves only', direct_reports: 'Activity about people who report to them', company: 'All activity' },
  'hr.correction.review': { company: 'Everyone in the company' },
  'hr.benefit.manage': { company: 'Everyone in the company' },
  'hr.lifecycle.manage': { company: 'Everyone in the company' },
  'hr.checklist.manage': { company: 'Everyone in the company' },
  'hr.checklist.assign': { company: 'Everyone in the company' },
  'hr.document.manage': { company: 'Everyone in the company' },
  'hr.grant.manage': { company: 'Everyone in the company' },
};

export function editablePermission(capability: string): EditablePermission | undefined {
  return EDITABLE_PERMISSIONS.find((p) => p.capability === capability);
}
export function editableScopeLabel(capability: string, scope: string): string {
  return EDITABLE_SCOPE_LABELS[capability]?.[scope as AccessScope] ?? SCOPE_LABELS[scope] ?? scope;
}

// ── Oct 20 final UAT batch: the COMPACT editor wording ─────────────────────────────────────────────────────────────────────────────────
// PRESENTATION ONLY, and kept as SEPARATE maps (not extra fields on EDITABLE_PERMISSIONS) so the permission table above - the capability codes, the valid scopes and the
// plain-language meanings the tests pin - is untouched. These give each permission a short name for the one-row-per-permission editor and the summary badges, and a
// short reach for the row ("Whole company"). The longer wording above still appears when a row is expanded. Nothing here is sent to the server.
export const EDITABLE_SHORT_LABELS: Record<string, string> = {
  'hr.team.view': 'Team info',
  'hr.org.manage': 'HR management',
  'hr.audit.view': 'Activity log',
  'hr.correction.review': 'Correction requests',
  'hr.benefit.manage': 'Manage benefits',
  'hr.lifecycle.manage': 'Manage offboarding',
  'hr.checklist.manage': 'Checklist templates',
  'hr.checklist.assign': 'Employee checklists',
  'hr.document.manage': 'Forms & equipment',
  'hr.grant.manage': 'Manage access',
};
export const EDITABLE_SHORT_SCOPE_LABELS: Record<string, Partial<Record<AccessScope, string>>> = {
  'hr.team.view': { direct_reports: 'Direct reports', company: 'Whole company' },
  'hr.org.manage': { company: 'Whole company' },
  'hr.audit.view': { self: 'Own activity only', direct_reports: 'Direct reports', company: 'Whole company' },
  'hr.correction.review': { company: 'Whole company' },
  'hr.benefit.manage': { company: 'Whole company' },
  'hr.lifecycle.manage': { company: 'Whole company' },
  'hr.checklist.manage': { company: 'Whole company' },
  'hr.checklist.assign': { company: 'Whole company' },
  'hr.document.manage': { company: 'Whole company' },
  'hr.grant.manage': { company: 'Whole company' },
};
const DEFAULT_SHORT_SCOPE: Record<AccessScope, string> = { self: 'Themselves only', direct_reports: 'Direct reports', company: 'Whole company' };
export function editableShortLabel(capability: string): string {
  return EDITABLE_SHORT_LABELS[capability] ?? CAPABILITY_LABELS[capability] ?? capability;
}
export function editableShortScopeLabel(capability: string, scope: string): string {
  return EDITABLE_SHORT_SCOPE_LABELS[capability]?.[scope as AccessScope] ?? DEFAULT_SHORT_SCOPE[scope as AccessScope] ?? scope;
}

/** Plain wording for the server's refusal reasons on a save (the codes come from the existing grant rules). */
export function accessRefusalMessage(reason: string): string {
  switch (reason) {
    case 'no-self-grant': return 'You can’t change your own access.';
    case 'owner-only-capability': return 'Only the Primary Owner can change that permission.';
    case 'capability-not-granted': return 'You don’t have permission to change access.';
    default: return reason;
  }
}

export function capabilityLabel(code: string): string {
  return CAPABILITY_LABELS[code] ?? code;
}
export function scopeLabel(code: string): string {
  return SCOPE_LABELS[code] ?? code;
}
